<script setup lang="ts">
import { resolverWrapper, useAbortController } from '@/composables';
import { ChangeLogDto } from '@/models';
import { useVersionStore } from '@/store/version.store';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { RefreshIcon } from '@heroicons/vue/solid';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:show']);
const changes = ref<Array<ChangeLogDto>>([]);
const isOpen = ref(false);
const { controller, abort } = useAbortController();

const versionStore = useVersionStore();
function refreshPage() {
    window.location.reload();
}

watch(
    async () => versionStore.releasedVersion,
    async () => {
        await getChangeLogsVersion();
        isOpen.value = !versionStore.updatedVersion;
    }
);
onMounted(async () => {
    await versionStore.getReleasedVersion();
    await getChangeLogsVersion();
    isOpen.value = !versionStore.updatedVersion;
});
async function getChangeLogsVersion() {
    if (versionStore.updatedVersion) return;
    await resolverWrapper(
        versionStore.systemApi.systemConfigChangeLogs(
            versionStore.releasedVersion,

            { signal: controller.value.signal }
        ),
        (response) => {
            changes.value = response.data ?? [];
        },
        {
            reject: (e) => {},
        }
    );
}
</script>
<template>
    <TransitionRoot appear :show="isOpen" as="template">
        <Dialog as="div" class="relative z-50" static @close="() => null">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black/70 backdrop-blur-sm" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white/90 p-6 text-left align-middle shadow-xl transition-all relative"
                        >
                            <!-- Decorative elements -->
                            <div
                                class="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-indigo-100/50"
                            ></div>
                            <div
                                class="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-indigo-100/30"
                            ></div>

                            <!-- Icon with smooth scale animation -->
                            <div
                                class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 mb-4"
                            >
                                <RefreshIcon class="h-8 w-8 text-indigo-600 smooth-pulse" />
                            </div>

                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900 text-center"
                            >
                                New Update Available!
                            </DialogTitle>
                            <div class="mt-2">
                                <p class="text-sm text-gray-500 text-center">
                                    We've made important improvements. Refresh to get the latest
                                    features and fixes.
                                </p>
                                <ul class="list-disc text-sm text-gray-500 pt-2 px-8">
                                    <template v-for="e in changes" :key="e.id">
                                        <li>
                                            {{ e.changeDescription }}
                                        </li>
                                    </template>
                                </ul>
                            </div>

                            <div class="mt-6 flex justify-center space-x-3">
                                <button
                                    type="button"
                                    class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-300 hover:scale-[1.03] active:scale-95"
                                    @click="refreshPage"
                                >
                                    <RefreshIcon class="-ml-1 mr-2 h-4 w-4" />
                                    Refresh Now
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<style scoped>
/* Custom smooth pulse animation */
.smooth-pulse {
    animation: smoothPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    transform-origin: center;
}

@keyframes smoothPulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.6);
        opacity: 0.9;
    }
}

/* Smooth transitions for buttons */
button {
    transition-property: transform, background-color, box-shadow;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Optimized backdrop filter */
.backdrop-blur-sm {
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
}
</style>
